import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { Button, HStack, Modal, theme } from '@sg/garnish';

export const CreditCardFormFooter = (props: CreditCardFormFooterProps) => {
  const { mode, isSaving, isDeleting, canSubmit, onDelete, onSubmit } = props;

  const { formatMessage } = useIntl();
  const isEditing = mode === 'edit';

  return (
    <Modal.Footer style={styles.footer} withoutTopBorder>
      <HStack itemsPerRow={isEditing ? 2 : 1} gap={theme.spacing['4']}>
        {isEditing ? (
          <View style={isEditing ? styles.ctaWrapperFluid : styles.ctaWrapper}>
            <Button
              testID="credit-card-form.remove"
              size="large-wide"
              palette="caution"
              style={isEditing ? styles.ctaFluid : undefined}
              isLoading={isDeleting}
              accessibilityRole="button"
              accessibilityLabel={formatMessage(messages.remove)}
              accessibilityHint={formatMessage(messages.remove)}
              onPress={onDelete}
            >
              {formatMessage(messages.remove)}
            </Button>
          </View>
        ) : null}

        <View style={isEditing ? styles.ctaWrapperFluid : styles.ctaWrapper}>
          <Button
            testID="credit-card-form.submit"
            size="large-wide"
            palette="primary"
            style={isEditing ? styles.ctaFluid : undefined}
            isLoading={isSaving}
            disabled={!canSubmit}
            accessibilityRole="button"
            accessibilityLabel={formatMessage(messages.save)}
            accessibilityHint={formatMessage(messages.save)}
            accessibilityState={{ disabled: !canSubmit }}
            onPress={onSubmit}
          >
            {formatMessage(messages.save)}
          </Button>
        </View>
      </HStack>
    </Modal.Footer>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardFormFooterProps = Readonly<{
  mode: 'add' | 'edit' | 'replace';
  isSaving?: boolean;
  isDeleting?: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  onDelete?: () => void;
}>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    padding: 0,
  },
  ctaWrapper: {
    alignSelf: 'center',
  },
  ctaWrapperFluid: {
    width: '100%',
  },
  ctaFluid: {
    paddingHorizontal: theme.spacing['4'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  remove: {
    defaultMessage: 'Remove card',
    description: 'Credit Card Form Footer | Remove button text',
  },
  save: {
    defaultMessage: 'Save card',
    description: 'Credit Card Form Footer | Save button text',
  },
});
